// Configuração centralizada de variáveis de ambiente
export const config = {
  api: {
    // Usar variável de ambiente, sem fallback hardcoded
    baseUrl: process.env.REACT_APP_API_URL || '',
    websocketUrl: process.env.REACT_APP_WEBSOCKET_URL || '',
  },
  execution: {
    limit: parseInt(process.env.REACT_APP_EXECUTION_LIMIT || '25000'),
  },
  app: {
    name: process.env.REACT_APP_NAME || 'Klinvia',
    shortName: process.env.REACT_APP_SHORT_NAME || 'Klinvia',
    description: process.env.REACT_APP_DESCRIPTION || 'Sistema de gestão clínica — agenda, prontuário, financeiro e atendimento integrado.',
    /** Slug do parceiro white-label (ex.: abo → logo em /public/partners/abo.png no menu lateral). */
    partnerName: (process.env.REACT_APP_PARTNER_NAME || '').trim().toLowerCase(),
    logo: process.env.REACT_APP_LOGO || '/principal-logo.png',
    platformName: process.env.REACT_APP_PLATFORM_NAME || 'Klinvia',
    companyName: process.env.REACT_APP_COMPANY_NAME || 'Klinvia',
    copyright: process.env.REACT_APP_COPYRIGHT || '© 2026 Klinvia. Todos os direitos reservados.',
  },
  production: {
    // Usar variáveis de ambiente, sem fallback hardcoded
    backendUrl: process.env.REACT_APP_BACKEND_URL || '',
    frontendUrl: process.env.REACT_APP_FRONTEND_URL || '',
  },
  revenue: {
    // Valor de cada conversão em BRL (R$)
    conversionValueBRL: parseFloat(process.env.REACT_APP_CONVERSION_VALUE_BRL || '0'),
    // Ativar/desativar cards de faturamento
    showRevenueCards: process.env.REACT_APP_SHOW_REVENUE_CARDS === 'true',
  },
  /**
   * Painel central de cobrança da Klinvia. Só o build do deploy interno deve
   * ligar isto — no painel de um cliente o menu não pode nem aparecer, e o
   * backend correspondente só existe com PLATFORM_ADMIN_ENABLED=true.
   */
  platformAdminEnabled: process.env.REACT_APP_PLATFORM_ADMIN_ENABLED === 'true',
  /** Clientes de teste para vendas (unidade demo + inbox mockado). */
  demoTestEnvEnabled: process.env.REACT_APP_DEMO_TEST_ENV_ENABLED === 'true',
  /**
   * Aba BirdID / VIDaaS no diálogo de assinatura. Exige credenciais no backend
   * (BIRDID_* / VIDAAS_*) e esta flag no build do frontend.
   */
  cloudSignatureEnabled: process.env.REACT_APP_CLOUD_SIGNATURE_ENABLED === 'true',
  /** Iframe do pipeline de formulários CRM — só visível com platformAdminEnabled. */
  platformCrmFormsUrl:
    process.env.REACT_APP_PLATFORM_CRM_FORMS_URL ||
    'https://barao-klinvia-orcamentos.i2pq5v.easypanel.host/?aba=crm&pipeline=formulario',
  /** Embedded Signup Meta: slug do tenant (ex.: clinica em api.clinica.seudominio.com). */
  embeddedSignupTenantSubdomain: process.env.REACT_APP_EMBEDDED_SIGNUP_TENANT_SUBDOMAIN || '',
  /**
   * URL do POST /complete no backend do tenant. Use {tenant} como placeholder.
   * Ex.: https://api.{tenant}.pooh.pro/api/v1/whatsapp-cloud-api/embedded-signup/complete
   * ou https://api.{tenant}.poohapp.com.br/.../complete
   *
   * Obrigatório no build do host que serve /facebook/callback (ex.: app.pooh.pro).
   * O painel do cliente (ex.: modelo.pooh.pro) só grava o state no próprio API; o callback
   * no app canónico é que precisa desta variável para encaminhar code+state para api.{slug}.pooh.pro.
   */
  embeddedSignupCompleteUrlTemplate:
    process.env.REACT_APP_EMBEDDED_SIGNUP_COMPLETE_URL_TEMPLATE || '',

  /**
   * URL absoluta do **launcher canônico** que abre o popup da Meta e escuta o postMessage
   * (ex.: https://app.pooh.pro/embedded-signup-launcher). Configure no build do painel de
   * cada cliente (modelo.pooh.pro, …). Quando definido, o botão de Conectar redireciona
   * o utilizador para esta URL em vez de abrir o popup no próprio domínio — assim só
   * `app.pooh.pro` precisa estar em "Allowed Domains" / OAuth Redirect URIs na Meta.
   */
  embeddedSignupLauncherUrl: process.env.REACT_APP_EMBEDDED_SIGNUP_LAUNCHER_URL || '',
  /** Teleconsulta: gravação no browser (áudio da aba). Desligado por padrão. */
  teleconsult: {
    browserRecording: process.env.REACT_APP_TELECONSULT_BROWSER_RECORDING === 'true',
    autoStartBrowserRecording:
      process.env.REACT_APP_TELECONSULT_BROWSER_RECORDING === 'true' &&
      process.env.REACT_APP_TELECONSULT_AUTO_BROWSER_RECORDING !== 'false',
    /** Jibri — ligar com REACT_APP_TELECONSULT_AUTO_RECORDING=true */
    autoStartRecording: process.env.REACT_APP_TELECONSULT_AUTO_RECORDING === 'true',
  },
};

/** Subdomínios do host público que não são tenant (painel do cliente). */
const RESERVED_EMBEDDED_SIGNUP_HOST_LABELS = new Set([
  'app',
  'www',
  'api',
  'cdn',
  'static',
  'mail',
]);

/**
 * Infere o slug do tenant a partir do hostname do painel (ex.: modelo.pooh.pro → modelo).
 * Evita depender só de REACT_APP_EMBEDDED_SIGNUP_TENANT_SUBDOMAIN em cada build do cliente.
 */
export function inferEmbeddedSignupTenantFromHost(hostname?: string): string {
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
  if (!host) return '';

  const patterns: RegExp[] = [
    /^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)\.pooh\.pro$/,
    /^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)\.poohapp\.com\.br$/,
  ];

  for (const re of patterns) {
    const m = host.match(re);
    if (m?.[1] && !RESERVED_EMBEDDED_SIGNUP_HOST_LABELS.has(m[1])) {
      return m[1];
    }
  }

  return '';
}

export const getApiUrl = (endpoint: string = '') => {
  // Se baseUrl não estiver configurado, construir a partir de BACKEND_URL
  let baseUrl = config.api.baseUrl;
  
  if (!baseUrl && config.production.backendUrl) {
    // Construir URL da API a partir do backend URL
    baseUrl = `${config.production.backendUrl}/api/v1`;
  }
  
  if (!baseUrl) {
    console.warn('⚠️ REACT_APP_API_URL ou REACT_APP_BACKEND_URL não configurado');
    return '';
  }
  
  // Remover barra final do baseUrl se existir
  const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
  if (!endpoint) return cleanBaseUrl;
  
  // Garantir que endpoint comece com / e remover barras duplicadas
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // Combinar baseUrl e endpoint, evitando barras duplas
  return `${cleanBaseUrl}${cleanEndpoint}`;
};

export const getWebSocketUrl = () => { 
  return config.api.websocketUrl; 
};

export const isProduction = () => { 
  return process.env.NODE_ENV === 'production'; 
};

export const getBackendBaseUrl = () => {
  // Usar variável de ambiente REACT_APP_BACKEND_URL
  if (config.production.backendUrl) {
    return config.production.backendUrl;
  }
  
  // Se não tiver BACKEND_URL, tentar extrair de API_URL
  if (config.api.baseUrl) {
    // Remover /api/v1 se existir
    const baseUrl = config.api.baseUrl.replace(/\/api\/v1\/?$/, '');
    return baseUrl;
  }
  
  console.warn('⚠️ REACT_APP_BACKEND_URL não configurado');
  return '';
};

export const getAppName = () => {
  return config.app.name;
};

export const getAppShortName = () => {
  return config.app.shortName;
};

export const getPlatformName = () => {
  return config.app.platformName;
};

export const getCompanyName = () => {
  return config.app.companyName;
};

export const getCopyright = () => {
  return config.app.copyright;
};

export const getAppLogo = () => {
  return config.app.logo;
};

export const getAppDescription = () => {
  return config.app.description;
};
